import React from 'react'
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  TimeScale,
  Tooltip,
  Plugin,
  ChartOptions,
  ChartData
} from 'chart.js'
import { Chart, } from 'react-chartjs-2'
import annotationPlugin from 'chartjs-plugin-annotation'
import 'chartjs-adapter-moment'
import { useGlobalState } from 'state'
import { StageAlertLevel, StageForecastHours } from 'common/consts'
import { ObservatoryValue, ObservatoryRow, ObservatoryType } from 'common/types'
import { isPrivateMode, isYosokuMode } from 'common/config'

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  TimeScale,
  Tooltip,
  annotationPlugin
)

type StageChartProps = {
  stage: ObservatoryRow
  height: number
  timeSeries: ObservatoryValue[]
}

export const StageChart = ({stage, height, timeSeries}: StageChartProps) => {
  const state = useGlobalState()
  const [chartData, setChartData] = React.useState<ChartData>({labels: [], datasets: []})
  const [chartOptions, setChartOptions] = React.useState<ChartOptions>({})
  const [plugins, setPlugins] = React.useState<Plugin[]>([])
  const chartRef = React.useRef<ChartJS>(null)

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      chartRef.current?.update()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  React.useEffect(() => {
    loadTimeSeries()
  }, [timeSeries])

  React.useEffect(() => {
    setPlugins(getPlugins())
  }, [stage])

  const getStageLevelAnnotations = () => {
    const annotations: any = {}
    const getAnnotation = (value: number, color: string) => {
      return {
        type: 'line',
        scaleID: 'yLevels',
        value: value,
        borderColor: color,
        borderWidth: 1.5
      }
    }
    if (stage.outbreakLevel !== undefined) {
      annotations.outbreakLevel = getAnnotation(stage.outbreakLevel, StageAlertLevel.OUTBREAK.color)
    }
    if (stage.floodLevel !== undefined) {
      annotations.floodLevel = getAnnotation(stage.floodLevel, StageAlertLevel.FLOOD.color)
    }
    if (stage.dangerousLevel !== undefined) {
      annotations.dangerousLevel = getAnnotation(stage.dangerousLevel, StageAlertLevel.DANGEROUS.color)
    }
    if (stage.evacuationLevel !== undefined) {
      annotations.evacuationLevel = getAnnotation(stage.evacuationLevel, StageAlertLevel.EVACUATION.color)
    }
    if (stage.warningLevel !== undefined) {
      annotations.warningLevel = getAnnotation(stage.warningLevel, StageAlertLevel.WARNING.color)
    }
    if (stage.standbyLevel !== undefined) {
      annotations.standbyLevel = getAnnotation(stage.standbyLevel, StageAlertLevel.STANDBY.color)
    }
    if (stage.warnLevel !== undefined) {
      annotations.warnLevel = getAnnotation(stage.warnLevel, StageAlertLevel.WARN.color)
    }
    if (stage.startLevel !== undefined) {
      annotations.startLevel = getAnnotation(stage.startLevel, StageAlertLevel.START.color)
    }
    if (stage.maxExperiencedLevel !== undefined) {
      annotations.maxExperiencedLevel = getAnnotation(stage.maxExperiencedLevel, StageAlertLevel.TIDEMAX.color)
    }
    return annotations
  }

  const getMaxStageLevel = (): number => {
    if (stage.outbreakLevel !== undefined) {
      return stage.outbreakLevel
    } else if (stage.floodLevel !== undefined) {
      return stage.floodLevel
    } else if (stage.dangerousLevel !== undefined) {
      return stage.dangerousLevel
    } else if (stage.evacuationLevel !== undefined) {
      return stage.evacuationLevel
    } else if (stage.warningLevel !== undefined) {
      return stage.warningLevel
    } else if (stage.standbyLevel !== undefined) {
      return stage.standbyLevel
    } else {
      return 0
    }
  }

  const getMaxLevel = (): number => {
    let maxLevel = getMaxStageLevel()
    if (stage.landform !== undefined && stage.landform.length > 0) {
      const yArr = stage.landform.map(lf => lf[1])
      const landformMaxLevel = Math.max(...yArr)
      if (maxLevel < landformMaxLevel) {
        maxLevel = landformMaxLevel
      }
    }
    const levels = timeSeries.map(ts => ts.level).filter(level => level !== undefined) as number[]
    const tsMaxLevel = Math.max(...levels)
    if (maxLevel < tsMaxLevel) {
      maxLevel = tsMaxLevel
    }
    maxLevel += 1
    if (stage.type === ObservatoryType.Tide && stage.id === '99_00000_1_100028' && maxLevel < 3) {
      return 3
    } else {
      return Math.ceil(maxLevel + 1)
    }
  }

  const getMinLevel = (): number => {
    if (stage.type === ObservatoryType.Tide && stage.id === '99_00000_1_100028') {
      return -3
    }
    let minLevel = 0
    if (stage.landform !== undefined && stage.landform.length > 0) {
      const yArr = stage.landform.map(lf => lf[1])
      minLevel = Math.min(...yArr)
    } else {
      const levels = timeSeries.map(ts => ts.level).filter(level => level !== undefined) as number[]
      minLevel = Math.min(...levels)
    }
    return Math.floor(minLevel - 1)
  }

  const getMinDate = React.useCallback((): string => {
    return state.app.baseDate.subtract(isYosokuMode ? 35 : 200, 'minutes').local().format('YYYY/MM/DD HH:mm')
  }, [state.app.baseDate])

  const getMaxDate = React.useCallback((): string => {
    let hours = 0
    if (isPrivateMode) {
      hours = StageForecastHours
    } else if (isYosokuMode) {
      hours = 1
    }
    return state.app.baseDate.add(hours, 'hours').add(5, 'minutes').local().format('YYYY/MM/DD HH:mm')
  }, [state.app.baseDate])

  const loadTimeSeries = () => {
    const labels: string[] = []
    const levels: (number | null)[] = []
    const reals: (number | null)[] = []
    const forecasts: (number | null)[] = []

    timeSeries.forEach(ts => {
      labels.push(ts.date)
      const level = ts.level === undefined ? null : Number(ts.level.toFixed(2))
      levels.push(level)
      if (ts.type === 'real') {
        reals.push(level)
        forecasts.push(null)
      } else {
        reals.push(null)
        forecasts.push((isPrivateMode || isYosokuMode) ? level : null)
      }
    })

    const maxLevel = getMaxLevel()
    const minLevel = getMinLevel()

    const arr = timeSeries.filter(ts => ts.type === 'real' && ts.level !== undefined)
    let realLatestLevel = arr.length > 0 ? arr.slice(-1)[0].level : null
    if (realLatestLevel === null && stage.level !== undefined) {
      realLatestLevel = stage.level
    }

    setChartOptions({
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        xDate: {
          type: 'time',
          time: {
            tooltipFormat: 'MM/DD HH:mm',
            parser: 'YYYY/MM/DD HH:mm',
            unit: 'hour',
            minUnit: 'minute',
            displayFormats: {
              'hour': 'HH:mm',
            }
          },
          min: getMinDate(),
          max: getMaxDate()
        },
        yLevels: {
          ticks: {
            stepSize: 1
          },
          max: maxLevel,
          min: minLevel
        }
      },
      plugins: {
        filler: {
          drawTime: 'beforeDatasetsDraw'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || ''
              if (label) {
                label += ': '
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y + 'm'
              }
              return label
            }
          }
        },
        annotation: {
          annotations: {
            ...getStageLevelAnnotations(),
            nowLine: {
              borderColor: '#FF0000',
              borderWidth: 1.5,
              type: 'line',
              scaleID: 'xDate',
              value: state.app.baseDate.local().format('YYYY/MM/DD HH:mm'),
              label: {
                content: '現在',
                position: 'start',
                display: true
              }
            },
            waterBox: {
              type: 'box',
              drawTime: 'beforeDatasetsDraw',
              yScaleID: 'yLevels',
              borderColor: 'rgba(0,0,0,0)',
              yMin: realLatestLevel !== null ? realLatestLevel : minLevel,
              yMax: minLevel,
              backgroundColor: '#3D8AFF'
            },
          }
        }
      }
    })

    setChartData({
      labels,
      datasets: [
        {
          type: 'line' as const,
          label: '水位',
          pointBackgroundColor: 'white',
          fill: false,
          xAxisID: 'xDate',
          yAxisID: 'yLevels',
          borderWidth: 2,
          borderColor: '#01163F',
          backgroundColor: '#01163F',
          pointBorderColor: '#01163F',
          pointRadius: 2,
          pointHitRadius: 0,
          data: reals,
          spanGaps: true
        },
        {
          type: 'line' as const,
          label: '水位',
          pointBackgroundColor: 'white',
          fill: false,
          xAxisID: 'xDate',
          yAxisID: 'yLevels',
          borderWidth: 3,
          borderColor: '#3734dc',
          backgroundColor: '#3734dc',
          pointBorderColor: '#3734dc',
          pointRadius: 0,
          pointHitRadius: 0,
          borderDash: [10, 5],
          data: forecasts,
          spanGaps: true
        }
      ]
    })
  }

  const getPlugins = (): Plugin[] => {
    const landform = stage.landform === undefined ? [] : stage.landform
    if (landform.length <= 0) {
      return []
    }
    return [{
      id: 'landform',
      beforeDatasetsDraw: chart => {
        if (!chart.scales.xDate || !chart.scales.yLevels) {
          return
        }
        const ctx = chart.ctx
        if (ctx === null) return
        const xAxe = chart.scales.xDate
        const yAxe = chart.scales.yLevels
        const minGround = Math.min(...landform.map(row => row[0]))
        const maxGround = Math.max(...landform.map(row => row[0]))
        ctx.strokeStyle = 'red'
        ctx.beginPath()
        ctx.moveTo(xAxe.left, yAxe.bottom)
        for (let i = 0; i < landform.length; i++) {
          let x = landform[i][0] - minGround
          x = (x / (maxGround - minGround)) * xAxe.width + xAxe.left
          let y = yAxe.max - yAxe.min - (landform[i][1] - yAxe.min)
          y = (y / (yAxe.max - yAxe.min)) * yAxe.height + yAxe.top
          ctx.lineTo(x, y)
        }
        ctx.lineTo(xAxe.right, yAxe.bottom)
        ctx.lineTo(xAxe.left, yAxe.bottom)
        ctx.closePath()
        ctx.fillStyle = '#f1eeea'
        ctx.fill()
      }
    }]
  }

  return <Chart ref={chartRef} type='line' data={chartData} height={height} options={chartOptions} plugins={plugins} redraw />
}
